import { PlayCircleIcon } from '@langchain/untitled-ui-icons';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
} from '@mui/joy';

import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { hasMoreInfiniteItems } from '@/Pages/Conversation/utils/hasMoreInfiniteItems';
import Breadcrumbs from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { FeedbackChips } from '@/components/FeedbackChips';
import { PageTitle } from '@/components/PageTitle';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { LatencyChip } from '@/components/RunLatencyChip';
import {
  conversationsExtraSchema,
  getConversationExtraValues,
  getFormConversationsExtraValues,
} from '@/components/SessionCrudPane/SessionConversationExtra';
import {
  useOrganizationId,
  useRunStats,
  useRunsInfinite,
  useSession,
  useSessionUpdateMutation,
} from '@/hooks/useSwr';
import MessageChatIcon from '@/icons/MessageChatIcon.svg?react';
import { SessionSchema } from '@/types/schema';

import { getGraphUrlFromRun } from '../Graph/GraphThreadPage.utils';
import { RawTokensTooltip } from '../Run/components/TokensChip';
import { CHILD_RUNS_SELECT } from '../Run/utils/utils';
import { CollatedConversation } from './CollatedConversation';
import { THREAD_RUN_SELECT } from './constants';
import {
  EditConversationViewContext,
  EditConversationViewContextProvider,
} from './utils/EditConversationViewContext';

const formatter = new Intl.NumberFormat('en-US', {});
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 20,
});

function ConversationEditView(props: { session: SessionSchema }) {
  'use no memo';
  const setOpenContext = useContext(EditConversationViewContext);

  const defaultValues = useMemo(
    () => getFormConversationsExtraValues(props.session),
    [props.session]
  );

  const form = useForm({ defaultValues });

  const mutateSession = useSessionUpdateMutation(props.session.id, {
    onSuccess: () => setOpenContext?.setOpen(false),
  });

  const onSubmit = form.handleSubmit(async (data) => {
    await mutateSession.trigger({
      json: {
        extra: {
          ...props.session.extra,
          conversations: getConversationExtraValues(
            data as {
              input: string;
              output: string;
            }
          ),
        },
      },
    });
  });
  if (!setOpenContext) {
    return null;
  }

  return (
    <Popover open={setOpenContext.open} onOpenChange={setOpenContext.setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outlined" color="neutral">
          Edit view
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex min-w-[420px] flex-col gap-2" align="end">
        <div className="flex flex-col">
          <strong className="font-semibold">Path to values</strong>
          <span className="text-sm text-tertiary">
            If an object is present as an input / output, we use this path to
            extract a string value from payload. If not set, we use a key which
            has a list of messages or the longest string value instead.
          </span>
        </div>

        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <FormControl>
            <FormLabel>Input</FormLabel>
            <Input
              type="text"
              placeholder="JSON Path or empty for default heuristic"
              {...form.register('input')}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Output</FormLabel>
            <Input
              type="text"
              placeholder="JSON Path or empty for default heuristic"
              {...form.register('output')}
            />
          </FormControl>

          <Button type="submit" loading={form.formState.isLoading}>
            Save
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

export function Conversation() {
  const navigate = useNavigate();
  const {
    sessionId,
    conversationId: paramConversationId,
    datasetShareToken,
  } = useParams();
  const [searchParams] = useSearchParams();
  if (!sessionId) throw new Error('Session ID is required');

  const conversationId =
    paramConversationId ?? searchParams.get('conversationId');

  const [searchFilter, setSearchFilter] = useState<string | undefined>(
    undefined
  );

  const filterRuns = useRunsInfinite(
    searchFilter
      ? {
          filter: `and(${searchFilter}, eq(thread_id, "${conversationId}"))`,
          session: [sessionId],
          select: CHILD_RUNS_SELECT,
          skip_prev_cursor: true,
          order: 'asc',
        }
      : null,
    undefined,
    { keepPreviousData: false }
  );

  const rootRunsQuery = useRunsInfinite({
    session: [sessionId],
    is_root: true,
    filter: `eq(thread_id, "${conversationId}")`,
    order: 'asc',
    select: THREAD_RUN_SELECT,
    skip_prev_cursor: true,
  });

  useEffect(() => {
    if (searchFilter) {
      const hasMoreRootRuns = hasMoreInfiniteItems(rootRunsQuery);
      const allFilteredRuns = filterRuns.data?.flat();
      const allRootRuns = rootRunsQuery.data?.flat();
      const hasOrphanedRuns = allFilteredRuns?.some(
        (run) =>
          !allRootRuns?.some((rootRun) =>
            run.parent_run_ids?.includes(rootRun.id)
          )
      );
      if (
        hasOrphanedRuns &&
        hasMoreRootRuns &&
        !rootRunsQuery.isLoading &&
        !rootRunsQuery.isValidating
      ) {
        rootRunsQuery.setSize((size) => size + 1);
      }
    }
  }, [rootRunsQuery, filterRuns, searchFilter]);

  const project = useSession(sessionId);
  const userPlainOptions = conversationsExtraSchema.safeParse(
    project.data?.extra?.conversations
  );

  const plainOptions = userPlainOptions.success
    ? userPlainOptions.data
    : {
        input: { type: 'key-heuristic' as const },
        output: { type: 'key-heuristic' as const },
      };

  const rootRuns = rootRunsQuery.data?.flat() ?? [];
  const organizationId = useOrganizationId();

  const stats = useRunStats(
    {
      filter: `and(eq(thread_id, "${conversationId}"))`,
      is_root: true,
      session: [sessionId],
    },
    { datasetShareToken }
  );

  const graphUrl = getGraphUrlFromRun(organizationId, rootRuns[0]);

  if (rootRunsQuery.isLoading || project.isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  if (rootRunsQuery.error) {
    return <ErrorBanner>{rootRunsQuery.error.message}</ErrorBanner>;
  }

  if (!project.data) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  return (
    <EditConversationViewContextProvider>
      <div className="flex h-[100vh] w-full flex-col">
        <div className="flex-none px-4 pt-3">
          <Breadcrumbs />
        </div>

        <div className="flex flex-none items-center justify-between px-4">
          <PageTitle icon={<MessageChatIcon className="m-0.5 h-5 w-5" />}>
            Thread {conversationId}
          </PageTitle>

          <div className="flex items-center gap-2">
            {graphUrl != null && (
              <Button
                size="sm"
                variant="outlined"
                color="primary"
                startDecorator={<PlayCircleIcon className="size-4" />}
                onClick={() => navigate(graphUrl)}
              >
                LangGraph Studio
              </Button>
            )}
            <ConversationEditView session={project.data} />
          </div>
        </div>

        <div className="flex flex-none flex-wrap items-start justify-between gap-6 px-4 pb-4 pt-1">
          <div className="flex gap-6">
            <div className="flex flex-col items-start gap-1.5">
              <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                Trace count
              </div>
              <div>
                {stats.data?.run_count != null
                  ? formatter.format(stats.data?.run_count ?? 0)
                  : null}
              </div>
            </div>

            <RawTokensTooltip
              totalTokens={stats.data?.total_tokens}
              promptTokens={stats.data?.prompt_tokens}
              completionTokens={stats.data?.completion_tokens}
              promptCost={stats.data?.prompt_cost}
              completionCost={stats.data?.completion_cost}
            >
              <div className="flex flex-col items-start gap-1.5">
                <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                  Total Tokens
                </div>
                <div>
                  {formatter.format(stats.data?.total_tokens ?? 0)}
                  {stats.data?.total_cost != null && (
                    <span className="text-tertiary">
                      {' / '}
                      {usdFormatter.format(+stats.data.total_cost)}{' '}
                    </span>
                  )}
                </div>
              </div>
            </RawTokensTooltip>

            <div className="flex flex-col items-start gap-1.5">
              <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                Latency
              </div>
              <div className="flex items-center gap-2">
                <LatencyChip latency={stats.data?.latency_p50} label="P50: " />
                <LatencyChip latency={stats.data?.latency_p99} label="P99: " />
              </div>
            </div>
          </div>
          {Object.keys(stats.data?.feedback_stats ?? {}).length > 0 && (
            <div className="flex flex-col items-start gap-1.5">
              <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                Feedback
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <FeedbackChips feedbackStats={stats.data?.feedback_stats} />
              </div>
            </div>
          )}
        </div>

        {rootRunsQuery.isLoading && (
          <div>
            <LinearProgress />
          </div>
        )}

        <CollatedConversation
          rootRunsQuery={rootRunsQuery}
          plainOptions={plainOptions}
          filterRuns={filterRuns}
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          sessionId={sessionId}
        />
      </div>
    </EditConversationViewContextProvider>
  );
}
